import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Clock, LogOut } from 'lucide-react';
import { toast } from 'sonner';

// Tunables — change these if you want a different idle policy.
const IDLE_MS = 30 * 60 * 1000;   // 30 minutes of no activity → show warning
const WARN_MS = 60 * 1000;        // 60 seconds to respond before forced logout
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * IdleTimeout — auto sign-out after IDLE_MS of no user activity.
 * Renders nothing when no user is signed in. Shows a 60-second warning
 * dialog with a "Stay signed in" button before forcing logout.
 */
const IdleTimeout = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const [remaining, setRemaining] = useState(WARN_MS);

  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const countdownRef = useRef(null);
  // Throttle activity-handler resets to once per second to keep things cheap.
  const lastResetAtRef = useRef(0);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const performLogout = useCallback(() => {
    clearAllTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setWarningOpen(false);
    toast.message('Signed out due to inactivity. Please log in again.');
    navigate('/login');
  }, [clearAllTimers, setUser, navigate]);

  const startWarning = useCallback(() => {
    setRemaining(WARN_MS);
    setWarningOpen(true);
    // Live countdown for the dialog body
    const startedAt = Date.now();
    countdownRef.current = setInterval(() => {
      const left = Math.max(0, WARN_MS - (Date.now() - startedAt));
      setRemaining(left);
      if (left <= 0 && countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }, 1000);
    // Force logout when warning expires
    warnTimerRef.current = setTimeout(performLogout, WARN_MS);
  }, [performLogout]);

  const resetIdle = useCallback(() => {
    // If the warning dialog is already open we DON'T silently reset — the user
    // must explicitly click "Stay signed in" to dismiss it.
    if (warningOpen) return;
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    idleTimerRef.current = setTimeout(startWarning, IDLE_MS);
  }, [warningOpen, startWarning]);

  // Throttled activity handler attached to window
  const onActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastResetAtRef.current < 1000) return;
    lastResetAtRef.current = now;
    resetIdle();
  }, [resetIdle]);

  // Mount / unmount listeners only while a user is signed in.
  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setWarningOpen(false);
      return undefined;
    }
    resetIdle();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearAllTimers();
    };
  }, [user, resetIdle, onActivity, clearAllTimers]);

  const handleStay = () => {
    clearAllTimers();
    setWarningOpen(false);
    resetIdle();
  };

  if (!user) return null;
  const secondsLeft = Math.ceil(remaining / 1000);

  return (
    <Dialog open={warningOpen} onOpenChange={(v) => { if (!v) handleStay(); }}>
      <DialogContent data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Are you still there?
          </DialogTitle>
          <DialogDescription>
            For your security, you'll be signed out due to inactivity in
            {' '}<b data-testid="idle-warning-seconds">{secondsLeft}</b> second{secondsLeft === 1 ? '' : 's'}.
            Click <b>"Stay signed in"</b> to continue your session.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={performLogout}
            data-testid="idle-logout-now-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out now
          </Button>
          <Button
            onClick={handleStay}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid="idle-stay-signed-in-button"
          >
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IdleTimeout;
